import { z } from 'zod';

export const CADENCES = ['immediate', 'hourly', 'daily', 'weekly'] as const;
export type Cadence = (typeof CADENCES)[number];
export type DigestCadence = Exclude<Cadence, 'immediate'>;

export function isCadence(value: unknown): value is Cadence {
	return typeof value === 'string' && (CADENCES as readonly string[]).includes(value);
}

export const REGISTRATION_STATS = {
	count: { label: 'Registrations', kind: 'figure' },
	amount: { label: 'Amount paid', kind: 'figure' },
	addonsSold: { label: 'Add-ons sold', kind: 'figure' },
	byTicket: { label: 'By ticket', kind: 'breakdown' },
	byGroup: { label: 'By group', requiresGroups: true, kind: 'breakdown' }
} as const;
export type RegistrationStatKey = keyof typeof REGISTRATION_STATS;

export const REGISTRATION_COLUMNS = {
	name: { label: 'Name' },
	email: { label: 'Email' },
	group: { label: 'Group', requiresGroups: true },
	ticket: { label: 'Ticket' },
	addons: { label: 'Add-ons' },
	amount: { label: 'Amount' }
} as const;
export type RegistrationColumnKey = keyof typeof REGISTRATION_COLUMNS;

const statKeys = Object.keys(REGISTRATION_STATS) as [RegistrationStatKey, ...RegistrationStatKey[]];
const columnKeys = Object.keys(REGISTRATION_COLUMNS) as [
	RegistrationColumnKey,
	...RegistrationColumnKey[]
];

export const DIGEST_ROW_LIMIT = 5;

export const DIGEST_SECTIONS = {
	registrations: {
		key: 'registrations',
		title: 'Registrations',
		eventType: 'registration.completed',
		filters: ['userGroupIds', 'ticketIds', 'addonIds'],
		defaults: {
			stats: ['count', 'amount'] as RegistrationStatKey[],
			columns: ['name', 'group', 'ticket', 'addons', 'amount'] as RegistrationColumnKey[]
		}
	}
} as const;
export type DigestSectionKey = keyof typeof DIGEST_SECTIONS;
export type DigestSectionDef = (typeof DIGEST_SECTIONS)[DigestSectionKey];

export function digestSectionForEventType(type: string): DigestSectionKey | null {
	for (const def of Object.values(DIGEST_SECTIONS)) {
		if (def.eventType === type) return def.key;
	}
	return null;
}

export function isDigestableEventType(type: string): boolean {
	return digestSectionForEventType(type) !== null;
}

function isValidTimezone(value: string): boolean {
	try {
		new Intl.DateTimeFormat('en-US', { timeZone: value });
		return true;
	} catch {
		return false;
	}
}

const registrationsSectionConfig = z.object({
	stats: z.array(z.enum(statKeys)).min(1),
	columns: z.array(z.enum(columnKeys)).min(1),
	rowLimit: z.literal(DIGEST_ROW_LIMIT)
});

export const digestConfigSchema = z.object({
	timezone: z.string().refine(isValidTimezone, 'Unknown timezone'),
	sections: z.object({ registrations: registrationsSectionConfig.optional() })
});
export type DigestConfig = z.infer<typeof digestConfigSchema>;

export function defaultDigestConfig(eventTypes: string[], timezone: string): DigestConfig {
	const sections: DigestConfig['sections'] = {};
	for (const type of eventTypes) {
		if (digestSectionForEventType(type) === 'registrations') {
			sections.registrations = {
				stats: [...DIGEST_SECTIONS.registrations.defaults.stats],
				columns: [...DIGEST_SECTIONS.registrations.defaults.columns],
				rowLimit: DIGEST_ROW_LIMIT
			};
		}
	}
	return { timezone, sections };
}

const countByLabel = z.array(z.object({ label: z.string(), count: z.number().int() }));

export const digestRowSchema = z.object({
	attendeeId: z.string(),
	eventName: z.string().nullable(),
	name: z.string().nullable(),
	email: z.string().nullable(),
	group: z.string().nullable(),
	ticket: z.string().nullable(),
	addons: z.array(z.object({ label: z.string(), quantity: z.number().int() })),
	// Dollars. null means a group buyer paid for this place.
	amount: z.number().nullable(),
	completedAt: z.string()
});
export type DigestRow = z.infer<typeof digestRowSchema>;

export const registrationsSectionPayloadSchema = z.object({
	key: z.literal('registrations'),
	statKeys: z.array(z.enum(statKeys)),
	columns: z.array(z.enum(columnKeys)),
	stats: z.object({
		count: z.number().int().optional(),
		amount: z.number().optional(),
		addonsSold: z.number().int().optional(),
		byTicket: countByLabel.optional(),
		byGroup: countByLabel.optional()
	}),
	filtered: z.boolean(),
	rows: z.array(digestRowSchema),
	totalRows: z.number().int(),
	link: z.string()
});
export type RegistrationsSectionPayload = z.infer<typeof registrationsSectionPayloadSchema>;

export const digestPayloadSchema = z.object({
	subscriptionId: z.string(),
	organizationId: z.string(),
	organizationName: z.string(),
	scope: z.discriminatedUnion('kind', [
		z.object({ kind: z.literal('event'), eventId: z.string(), eventName: z.string() }),
		// A rule narrowed to several events. "All events" would misdescribe it.
		z.object({ kind: z.literal('events'), count: z.number().int() }),
		z.object({ kind: z.literal('all') })
	]),
	window: z.object({
		start: z.string(),
		end: z.string(),
		timezone: z.string(),
		cadence: z.enum(['hourly', 'daily', 'weekly'])
	}),
	currency: z.string(),
	sections: z.array(registrationsSectionPayloadSchema)
});
export type DigestPayload = z.infer<typeof digestPayloadSchema>;

// The seeded system rule that emails an event's owner about its first registration.
// Fixed so the event page can read its status without searching for it.
export const FIRST_REGISTRATION_SUBSCRIPTION_ID = '00000000-0000-0000-0000-000000000776';

export interface DigestSectionView {
	title: string;
	stats: Array<{ label: string; value: string; kind: 'figure' | 'breakdown' }>;
	table: { headers: string[]; rows: string[][] };
	moreLabel: string | null;
	link: { label: string; url: string };
	note: string | null;
}

export function formatNotificationMoney(amount: number, currency: string): string {
	const code = currency.length === 3 ? currency.toUpperCase() : 'USD';
	return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(amount);
}

function counts(list: Array<{ label: string; count: number }> | undefined): string {
	return (list ?? []).map((c) => `${c.label} ${c.count}`).join(' · ') || '—';
}

function statValue(key: RegistrationStatKey, stats: RegistrationsSectionPayload['stats'], currency: string): string {
	switch (key) {
		case 'count':
			return String(stats.count ?? 0);
		case 'amount':
			return formatNotificationMoney(stats.amount ?? 0, currency);
		case 'addonsSold':
			return String(stats.addonsSold ?? 0);
		case 'byTicket':
			return counts(stats.byTicket);
		case 'byGroup':
			return counts(stats.byGroup);
	}
}

function cell(key: RegistrationColumnKey, row: DigestRow, currency: string): string {
	switch (key) {
		case 'name':
			return row.name || row.email || 'Someone';
		case 'email':
			return row.email ?? '';
		case 'group':
			return row.group ?? '';
		case 'ticket':
			return row.ticket ?? '';
		case 'addons':
			return row.addons.map((a) => (a.quantity > 1 ? `${a.label} ×${a.quantity}` : a.label)).join(', ');
		case 'amount':
			if (row.amount === null) return 'Group order';
			return row.amount === 0 ? 'Free' : formatNotificationMoney(row.amount, currency);
	}
}

export function buildDigestView(payload: DigestPayload): DigestSectionView[] {
	const allEvents = payload.scope.kind !== 'event';
	return payload.sections.map((section) => {
		// Rendered in the section definition's own key order rather than the
		// stored order, which is whatever order the planner happened to click.
		const columns = (Object.keys(REGISTRATION_COLUMNS) as RegistrationColumnKey[]).filter((key) =>
			section.columns.includes(key)
		);
		const statKeys = (Object.keys(REGISTRATION_STATS) as RegistrationStatKey[]).filter((key) =>
			section.statKeys.includes(key)
		);
		const headers = [...(allEvents ? ['Event'] : []), ...columns.map((key) => REGISTRATION_COLUMNS[key].label)];
		const rows = section.rows.map((row) => [
			...(allEvents ? [row.eventName ?? ''] : []),
			...columns.map((key) => cell(key, row, payload.currency))
		]);
		const more = section.totalRows - section.rows.length;
		return {
			title: DIGEST_SECTIONS[section.key].title,
			stats: statKeys.map((key) => ({
				label: REGISTRATION_STATS[key].label,
				value: statValue(key, section.stats, payload.currency),
				kind: REGISTRATION_STATS[key].kind
			})),
			table: { headers, rows },
			moreLabel: more > 0 ? `and ${more} more` : null,
			link: { label: 'See all', url: section.link },
			note:
				section.filtered && section.statKeys.includes('amount')
					? 'Amount counts orders placed by the people shown. Group orders bought on their behalf are not included.'
					: null
		};
	});
}
